import { invokeEdgeFunction } from '../observability/invokeEdgeFunction';
import type { LatLng } from '../maps/types';
import { routeOdKey, sameRoutePoint, type OdPair } from './routeOdKey';
import {
  DIRECTIONS_MAX_GOOGLE_PAIRS_PER_PLAN,
  loadRouteTravelCache,
  upsertRouteTravelCache,
} from './routeTravelCache';
import type { ScheduleJobInput, ScheduleLegInput } from './types';

export type { OdPair } from './routeOdKey';

/** Matches Edge Function compute-route-matrix MAX_PAIRS. */
export const DIRECTIONS_PREFETCH_BATCH = 100;

export interface PrefetchDirectionsResult {
  /** OD key → driving minutes (only successful cells / cache). */
  directionsMinutes: Record<string, number>;
  /** OD key → distance meters when Google returned them (not from duration-only cache). */
  directionsDistances: Record<string, number | null>;
  requestedPairs: number;
  resolvedPairs: number;
  cacheHits: number;
  fetchedPairs: number;
  /** True when any EF call failed (caller still falls back to static per miss). */
  hadError: boolean;
  /** True when uncached pairs exceeded per-plan Google cap. */
  rateLimited: boolean;
}

function dedupePoints(points: LatLng[]): LatLng[] {
  const seen = new Set<string>();
  const out: LatLng[] = [];
  for (const p of points) {
    const k = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

/**
 * Deadhead candidates for a day: every unique dropoff → every unique pickup.
 * Same-point pairs omitted (0 travel).
 */
export function collectDeadheadOdPairs(
  jobs: ScheduleJobInput[],
  fixedLegsByVehicle: Record<string, ScheduleLegInput[]> = {}
): OdPair[] {
  const pickups: LatLng[] = [];
  const dropoffs: LatLng[] = [];

  for (const job of jobs) {
    for (const leg of job.legs) {
      pickups.push(leg.pickup);
      dropoffs.push(leg.dropoff);
    }
  }
  for (const legs of Object.values(fixedLegsByVehicle)) {
    for (const leg of legs) {
      pickups.push(leg.pickup);
      dropoffs.push(leg.dropoff);
    }
  }

  const uniquePickups = dedupePoints(pickups);
  const uniqueDropoffs = dedupePoints(dropoffs);
  const pairs: OdPair[] = [];
  const seen = new Set<string>();

  for (const from of uniqueDropoffs) {
    for (const to of uniquePickups) {
      if (sameRoutePoint(from, to)) continue;
      const key = routeOdKey(from, to);
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push({ from, to });
    }
  }
  return pairs;
}

type PairResult = { minutes: number | null; ok: boolean; distanceMeters?: number | null };

type EfPairsResponse = {
  success?: boolean;
  results?: PairResult[];
  error?: string;
};

async function fetchPairsFromEdge(
  pairs: OdPair[],
  options?: {
    routingPreference?: 'TRAFFIC_UNAWARE' | 'TRAFFIC_AWARE' | 'TRAFFIC_AWARE_OPTIMAL';
    departureTimeIso?: string;
  }
): Promise<{ minutes: Record<string, number>; distances: Record<string, number | null>; hadError: boolean }> {
  const minutes: Record<string, number> = {};
  const distances: Record<string, number | null> = {};
  let hadError = false;

  for (let i = 0; i < pairs.length; i += DIRECTIONS_PREFETCH_BATCH) {
    const batch = pairs.slice(i, i + DIRECTIONS_PREFETCH_BATCH);
    const body: Record<string, unknown> = {
      mode: 'pairs',
      pairs: batch.map((p) => ({ from: p.from, to: p.to })),
    };
    if (options?.routingPreference) {
      body.routingPreference = options.routingPreference;
    }
    if (options?.departureTimeIso) {
      body.departureTime = options.departureTimeIso;
    }

    const { data, error } = await invokeEdgeFunction<EfPairsResponse>('compute-route-matrix', {
      body,
      reportError: false,
    });

    if (error || !data?.success || !Array.isArray(data.results)) {
      hadError = true;
      continue;
    }

    for (let j = 0; j < batch.length; j++) {
      const cell = data.results[j];
      if (!cell?.ok || cell.minutes == null || !Number.isFinite(cell.minutes)) continue;
      const key = routeOdKey(batch[j].from, batch[j].to);
      minutes[key] = Math.max(0, Math.ceil(cell.minutes));
      distances[key] = cell.distanceMeters ?? null;
    }
  }

  return { minutes, distances, hadError };
}

/**
 * Resolve OD minutes: Postgres cache → Google Routes (capped) → write-through cache.
 * Misses after rate-limit / errors remain absent (static fallback in estimateTravelMinutes).
 *
 * When `departureTimeIso` / traffic-aware routing is set, cache is bypassed so ETA
 * reflects the requested departure (trip date + pickup time).
 */
export async function prefetchDirectionsTravelMinutes(
  pairs: OdPair[],
  options?: {
    departureTimeIso?: string;
    routingPreference?: 'TRAFFIC_UNAWARE' | 'TRAFFIC_AWARE' | 'TRAFFIC_AWARE_OPTIMAL';
    bypassCache?: boolean;
  }
): Promise<PrefetchDirectionsResult> {
  const directionsMinutes: Record<string, number> = {};
  const directionsDistances: Record<string, number | null> = {};
  if (pairs.length === 0) {
    return {
      directionsMinutes,
      directionsDistances,
      requestedPairs: 0,
      resolvedPairs: 0,
      cacheHits: 0,
      fetchedPairs: 0,
      hadError: false,
      rateLimited: false,
    };
  }

  const trafficAware =
    options?.routingPreference === 'TRAFFIC_AWARE' ||
    options?.routingPreference === 'TRAFFIC_AWARE_OPTIMAL';
  const bypassCache = options?.bypassCache === true || trafficAware || !!options?.departureTimeIso;

  const keys = pairs.map((p) => routeOdKey(p.from, p.to));
  const cached = bypassCache ? {} : await loadRouteTravelCache(keys);
  let cacheHits = 0;
  const misses: OdPair[] = [];

  for (const pair of pairs) {
    const key = routeOdKey(pair.from, pair.to);
    const hit = cached[key];
    if (typeof hit === 'number') {
      directionsMinutes[key] = hit;
      cacheHits += 1;
    } else {
      misses.push(pair);
    }
  }

  let rateLimited = false;
  let toFetch = misses;
  if (misses.length > DIRECTIONS_MAX_GOOGLE_PAIRS_PER_PLAN) {
    rateLimited = true;
    toFetch = misses.slice(0, DIRECTIONS_MAX_GOOGLE_PAIRS_PER_PLAN);
  }

  let fetchedPairs = 0;
  let hadError = false;

  if (toFetch.length > 0) {
    const fetched = await fetchPairsFromEdge(toFetch, {
      routingPreference: options?.routingPreference,
      departureTimeIso: options?.departureTimeIso,
    });
    hadError = fetched.hadError;
    const upserts: Array<{
      pair: OdPair;
      durationMinutes: number;
      distanceMeters?: number | null;
    }> = [];

    for (const pair of toFetch) {
      const key = routeOdKey(pair.from, pair.to);
      const mins = fetched.minutes[key];
      if (typeof mins !== 'number') continue;
      directionsMinutes[key] = mins;
      directionsDistances[key] = fetched.distances[key] ?? null;
      fetchedPairs += 1;
      upserts.push({
        pair,
        durationMinutes: mins,
        distanceMeters: fetched.distances[key],
      });
    }

    if (!bypassCache) {
      await upsertRouteTravelCache(upserts);
    }
  }

  return {
    directionsMinutes,
    directionsDistances,
    requestedPairs: pairs.length,
    resolvedPairs: Object.keys(directionsMinutes).length,
    cacheHits,
    fetchedPairs,
    hadError,
    rateLimited,
  };
}
